import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import {
  CreateOrganization,
  OrganizationList,
  TaskChooseOrganization,
  useAuth,
  useClerk,
  useOrganizationList,
  useSession,
} from "@clerk/react";
import { Building2, Globe2 } from "lucide-react";
import { commandClerkAppearance } from "@/lib/clerkAppearance";
import { CommandBootScreen } from "@/components/CommandBootScreen";
import { brandedHubHost, brandedHubUrl } from "@/lib/hosts";
import { peekCheckoutIntent } from "@/lib/billing";

/**
 * Required org onboarding for Command.
 *
 * Clerk prod has force_organization_selection=false so sessions stay **active**
 * (pending+choose-organization tasks caused access denied / 403 loops).
 * We still require an org client-side here.
 *
 * Stability rules:
 * 1. Wait for session + membership list before any create/select UI.
 * 2. Sole membership → setActive once (no create form).
 * 3. If a legacy choose-organization task remains → TaskChooseOrganization.
 * 4. Parent gates on !orgId only.
 */
export function OrgGate() {
  const { isLoaded: sessionLoaded, session } = useSession();
  const { orgId } = useAuth();
  const { setActive } = useClerk();
  const { isLoaded, userMemberships, userInvitations } = useOrganizationList({
    userMemberships: { infinite: true },
    userInvitations: { infinite: true },
  });
  const autoActivating = useRef(false);

  const wantsHosted =
    peekCheckoutIntent() === "team" ||
    new URLSearchParams(window.location.search).get("upgrade") === "team";
  const afterOrgUrl = wantsHosted ? "/?upgrade=team" : "/";

  const taskKey = session?.currentTask?.key;
  const memberships = userMemberships?.data ?? [];
  const soleOrgId =
    memberships.length === 1 ? memberships[0]?.organization?.id : undefined;
  const hasMemberships = memberships.length > 0;
  const hasInvites =
    isLoaded && (userInvitations?.data?.length ?? 0) > 0;

  // One membership, no active org → setActive once (stops create-org flash loop)
  useEffect(() => {
    if (!isLoaded || !sessionLoaded || orgId || autoActivating.current) return;
    if (!soleOrgId) return;
    autoActivating.current = true;
    void setActive({ organization: soleOrgId }).finally(() => {
      autoActivating.current = false;
    });
  }, [isLoaded, sessionLoaded, orgId, soleOrgId, setActive]);

  // Still hydrating session or org list — never flash CreateOrganization
  // Use the same boot chrome as ProtectedApp (no second “loading screen” design).
  if (!sessionLoaded || !isLoaded) {
    return <CommandBootScreen detail="Checking organizations…" />;
  }

  // Parent should unmount us once orgId is set; keep calm if racey
  if (orgId) {
    return <CommandBootScreen detail="Opening your workspace…" />;
  }

  // Auto-activating sole org
  if (soleOrgId) {
    return <CommandBootScreen detail="Opening your workspace…" />;
  }

  // Clerk session task (force organization selection) — stable single component
  if (taskKey === "choose-organization") {
    return (
      <OrgShell
        title="Create your organization"
        subtitle="Your org is your multi-agent workspace. The slug becomes your branded OpenGateway hub."
      >
        <TaskChooseOrganization
          redirectUrlComplete={afterOrgUrl}
          appearance={commandClerkAppearance}
        />
        <OrgSlugHint wantsHosted={wantsHosted} />
      </OrgShell>
    );
  }

  // Multiple orgs / invites → pick one (includes create)
  if (hasMemberships || hasInvites) {
    return (
      <OrgShell
        title={hasInvites ? "You have an invite" : "Select organization"}
        subtitle={
          hasInvites
            ? "Accept the invitation to join their hub, or create your own org."
            : "Pick a workspace to open Command."
        }
      >
        <OrganizationList
          hidePersonal
          // Shows pending org invitations + memberships; accepting joins the hub
          afterSelectOrganizationUrl={afterOrgUrl}
          afterCreateOrganizationUrl={afterOrgUrl}
          appearance={commandClerkAppearance}
        />
      </OrgShell>
    );
  }

  // Zero memberships, list loaded, no session task → create
  return (
    <OrgShell
      title="Create your organization"
      subtitle="Your org is your multi-agent workspace. The slug becomes your branded OpenGateway hub."
    >
      <CreateOrganization
        skipInvitationScreen
        afterCreateOrganizationUrl={afterOrgUrl}
        appearance={commandClerkAppearance}
      />
      <OrgSlugHint wantsHosted={wantsHosted} />
    </OrgShell>
  );
}

function OrgSlugHint({ wantsHosted }: { wantsHosted: boolean }) {
  return (
    <>
      <p className="mt-4 text-center text-[11px] leading-relaxed text-zinc-500">
        Pick a short org name. Edit the slug if you want a cleaner host — e.g.{" "}
        <code className="text-orange-300/90">{brandedHubHost("acme")}</code> →
        gateway for agents and teammates.
      </p>
      {wantsHosted ? (
        <p className="mt-2 text-center text-[11px] text-orange-300/90">
          Next: Stripe checkout ($9.99/mo) attaches to this org.
        </p>
      ) : null}
    </>
  );
}

function OrgShell({
  children,
  title,
  subtitle,
}: {
  children: ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-og-ink text-white">
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/3 top-0 h-[36rem] w-[36rem] -translate-x-1/2 rounded-full bg-orange-500/15 blur-[120px]" />
        <div className="absolute bottom-0 right-0 h-[24rem] w-[24rem] rounded-full bg-violet-600/15 blur-[100px]" />
      </div>

      <div className="mx-auto flex min-h-screen max-w-lg flex-col justify-center px-4 py-12">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-500/15 text-orange-400">
            <Building2 className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            {title}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-zinc-400">{subtitle}</p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-zinc-400">
            <Globe2 className="h-3.5 w-3.5 text-orange-400" />
            <span>
              example →{" "}
              <span className="font-medium text-orange-300">
                {brandedHubUrl("acme")}
              </span>
            </span>
          </div>
        </div>
        <div className="w-full">{children}</div>
      </div>
    </div>
  );
}
